import type { ExternalParticipantType } from './ExternalParticipants';
import type { ParticipationLayerKind } from './ParticipationLayer';
import type { RoomDefinition } from './RoomDefinition';

export const COMPANION_RUNTIME_KINDS = [
    'telegamez_turn_based',
    'telegamez_multi_companion',
    'third_party_speech_to_speech',
    'vendor_managed_avatar_agent',
    'external_text_agent',
] as const;

export type CompanionRuntimeKind = typeof COMPANION_RUNTIME_KINDS[number];

export const COMPANION_SESSION_STATES = [
    'idle',
    'listening',
    'thinking',
    'speaking',
    'streaming',
    'paused',
    'interrupted',
    'error',
    'ended',
] as const;

export type CompanionSessionState = typeof COMPANION_SESSION_STATES[number];

export const getProgramCompanionSourceId = (companionId: string): string => (
    `agent:${companionId.trim()}`
);

export const COMPANION_INPUT_MODES = [
    'ptt',
    'vad',
    'full_duplex',
    'external',
] as const;

export type CompanionInputMode = typeof COMPANION_INPUT_MODES[number];

export const COMPANION_OUTPUT_MODES = [
    'tts_chunks',
    'realtime_audio',
    'vendor_stream',
    'text_only',
] as const;

export type CompanionOutputMode = typeof COMPANION_OUTPUT_MODES[number];

export const COMPANION_TRANSCRIPT_MODES = [
    'full',
    'partial',
    'vendor_owned',
    'none',
] as const;

export type CompanionTranscriptMode = typeof COMPANION_TRANSCRIPT_MODES[number];

export const COMPANION_INTERRUPTION_MODES = [
    'soft',
    'hard',
    'vendor_specific',
] as const;

export type CompanionInterruptionMode = typeof COMPANION_INTERRUPTION_MODES[number];

export interface StartCompanionSessionInput {
    roomId: string;
    sessionId: string;
    roomDefinition: RoomDefinition;
    participantId?: string;
    companionId: string;
    capabilities: CompanionCapabilities;
    initialContext?: Record<string, unknown>;
}

export interface CompanionCapabilities {
    inputMode: CompanionInputMode;
    outputMode: CompanionOutputMode;
    transcriptMode: CompanionTranscriptMode;
    interruptionMode: CompanionInterruptionMode;
}

export type RoomGatewayParticipantType =
    | 'human'
    | 'internal_companion'
    | ExternalParticipantType;

export interface RoomGatewayParticipant {
    participantId: string;
    participantType?: RoomGatewayParticipantType;
    sessionId?: string;
    layer?: ParticipationLayerKind;
}

export interface RoomGatewayQueueEntry {
    participantId: string;
    position: number;
    participantType?: RoomGatewayParticipantType;
    sessionId?: string;
    layer?: ParticipationLayerKind;
}

export type CompanionRuntimeEvent =
    | { type: 'input.started'; sessionId: string; participantId?: string; timestamp: number }
    | { type: 'input.updated'; sessionId: string; transcriptDelta: string; timestamp: number }
    | { type: 'input.completed'; sessionId: string; transcript: string; timestamp: number }
    | { type: 'response.started'; sessionId: string; timestamp: number }
    | { type: 'response.text_delta'; sessionId: string; text: string; timestamp: number }
    | { type: 'response.audio_chunk'; sessionId: string; audioId: string; payload: string; timestamp: number }
    | { type: 'response.media_stream_ready'; sessionId: string; streamId: string; timestamp: number }
    | { type: 'response.completed'; sessionId: string; timestamp: number }
    | { type: 'playback.started'; sessionId: string; audioId?: string; timestamp: number }
    | { type: 'playback.completed'; sessionId: string; audioId?: string; timestamp: number }
    | { type: 'session.state_changed'; sessionId: string; state: CompanionSessionState; timestamp: number }
    | { type: 'interrupt.completed'; sessionId: string; timestamp: number }
    | { type: 'error'; sessionId: string; message: string; timestamp: number };

export type CompanionInputEvent =
    | { type: 'microphone.audio_chunk'; payload: ArrayBuffer; timestamp: number }
    | { type: 'transcript.final'; text: string; timestamp: number }
    | { type: 'call_in.audio'; payload: ArrayBuffer; timestamp: number }
    | { type: 'chat.message'; text: string; timestamp: number }
    | { type: 'system.directive'; name: string; payload?: Record<string, unknown>; timestamp: number };

export type RoomGatewayEvent =
    | {
        type: 'room_state';
        roomId: string;
        timestamp: number;
        state?: Record<string, unknown>;
        activeParticipants?: RoomGatewayParticipant[];
    }
    | {
        type: 'queue_update';
        roomId: string;
        timestamp: number;
        queue: RoomGatewayQueueEntry[];
        activeParticipantId?: string;
    }
    | {
        type: 'turn_granted';
        roomId: string;
        participantId: string;
        timestamp: number;
        expiresAt?: number;
        layer?: ParticipationLayerKind;
    }
    | {
        type: 'turn_ended';
        roomId: string;
        timestamp: number;
        participantId?: string;
        reason: string;
    }
    | {
        type: 'participant_joined';
        roomId: string;
        timestamp: number;
        participant: RoomGatewayParticipant;
    }
    | {
        type: 'participant_left';
        roomId: string;
        participantId: string;
        participantType?: RoomGatewayParticipantType;
        timestamp: number;
    };
